import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { UserAuthenticationService } from '../../../../../libs/administration/src/lib/services/user-authentication.service';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { UserModuleMetadata } from '@zayan-erp/administration';
import { LocalStorageManagerService, VersionInfoComponent } from '../../../../../libs/shared/src/index';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ChatbotComponent } from '../../../../../libs/shared/src/lib/components/chatbot/chatbot.component';

@Component({
  selector: 'app-welcome-screen',
  standalone: true,
  imports: [NgClass, RouterModule, NgbTooltipModule, ChatbotComponent, VersionInfoComponent, NgTemplateOutlet],
  templateUrl: './welcome-screen.component.html',
  styleUrl: './welcome-screen.component.scss'
})
export class WelcomeScreenComponent {
  #router = inject(Router);
  #authService = inject(UserAuthenticationService);
  #localStorageService = inject(LocalStorageManagerService);
  #user = this.#authService.getUser();
  user = this.#user;
  /** Super-admin flag — What's New footer link is super-admin only. */
  isSuperAdmin = this.#authService.isAdmin();
  availableModules: UserModuleMetadata[] = (this.#user?.userModuleRoles ?? [])
    .map((x: any) => JSON.parse(x.module.metadata) as UserModuleMetadata)
    .sort((a, b) => a.label.localeCompare(b.label));
  recentScreens = this.getRecentScreens();
  /** Module routes starred on this welcome screen (persisted per user). */
  favoriteRoutes = new Set(this.#localStorageService.getWelcomeFavoritesFromStorage());
  /** Rail filter: all modules vs favorites only (persisted per user). */
  viewMode: 'home' | 'favorites' = this.#localStorageService.getWelcomeViewFromStorage();
  Date = new Date();

  constructor() {
    this.#authService.refreshToken().subscribe(() => {
      // user/module roles may be populated after refresh; recompute safely
      this.#user = this.#authService.getUser();
      this.availableModules = (this.#user?.userModuleRoles ?? [])
        .map((x: any) => JSON.parse(x.module.metadata) as UserModuleMetadata)
        .sort((a, b) => a.label.localeCompare(b.label));
      this.recentScreens = this.getRecentScreens();
      this.favoriteRoutes = new Set(this.#localStorageService.getWelcomeFavoritesFromStorage());
      this.viewMode = this.#localStorageService.getWelcomeViewFromStorage();
    });
    // Signing out of the CRM lands here; opening the only module would be the CRM again and sign
    // the user straight back in through the still-live ERP session.
    const signedOutOfCrm = inject(ActivatedRoute).snapshot.queryParamMap.has('signedOutOf');
    if (this.availableModules.length === 1 && !signedOutOfCrm) {
      this.#router.navigate([this.availableModules[0].route]);
    }
  }

  /** Modules shown in the grid for the current rail filter. */
  get displayedModules(): UserModuleMetadata[] {
    if (this.viewMode !== 'favorites') return this.availableModules;
    return this.availableModules.filter(m => this.favoriteRoutes.has(m.route));
  }

  setView(view: 'home' | 'favorites'): void {
    this.viewMode = view;
    this.#localStorageService.setWelcomeViewToStorage(view);
  }

  isFavorite(route: string): boolean {
    return this.favoriteRoutes.has(route);
  }

  toggleFavorite(route: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.favoriteRoutes.has(route)) {
      this.favoriteRoutes.delete(route);
    } else {
      this.favoriteRoutes.add(route);
    }
    // New Set so Angular picks up the change for [checked] / filters.
    this.favoriteRoutes = new Set(this.favoriteRoutes);
    this.#localStorageService.setWelcomeFavoritesToStorage([...this.favoriteRoutes]);
  }

  logout(): void {
    this.#authService.logout();
  }
  colors = ['#2b7fff', '#00bc7d', '#fe9a00', '#615fff', '#ff2056', '#62748e', '#6f42c1'];

  getColor(index: number): string {
    return this.colors[index % this.colors.length];
  }

  /** Map stored routes → module label/icon from the user's assigned modules. */
  private getRecentScreens(): { route: string; label: string; icon: string }[] {
    return this.#localStorageService
      .getRecentScreensFromStorage()
      .map(route => {
        const module = this.availableModules.find(m => route === m.route || route.startsWith(`${m.route}/`));
        return module ? { route, label: module.label, icon: module.icon } : null;
      })
      .filter((item): item is { route: string; label: string; icon: string } => !!item);
  }
}
